import { createRule, defineRegleConfig, type Maybe } from '@regle/core';
import {
  required,
  minLength,
  maxLength,
  minValue,
  maxValue,
  numeric,
  withMessage
} from '@regle/rules';
import { useI18n } from 'vue-i18n';

// Custom email rule to replace Joi-based validation
export const emailRule = createRule({
  validator: (value: Maybe<string>) => {
    if (value === undefined || value === null || value === '') {
      return true; // Consider empty values as valid for this rule
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  },
  message: 'Invalid email format'
});
/**
 * This composable provides a centralized regle configuration for the application.
 * It maps standard regle rules to the localized error messages defined in the backend.
 */
export function useRegleConfig() {
  const { t } = useI18n();

  const { useRegle } = defineRegleConfig({
    rules: () => ({
      // Required rule
      required: withMessage(required, () => t('errors.fields.required')),

      // Min length rule
      minLength: withMessage(minLength, ({ $params: [min] }) =>
        t('errors.fields.minChar', { value: min })
      ),

      // Max length rule
      maxLength: withMessage(maxLength, ({ $params: [max] }) =>
        t('errors.fields.maxChar', { value: max })
      ),

      // Minimum value rule
      minValue: withMessage(minValue, ({ $params: [min] }) =>
        t('errors.fields.minValue', { value: min })
      ),

      // Maximum value rule
      maxValue: withMessage(maxValue, ({ $params: [max] }) =>
        t('errors.fields.maxValue', { value: max })
      ),

      // Email format rule
      email: withMessage(emailRule, () => t('errors.component.invalidEmailFormat')),

      // Numeric only rule
      numeric: withMessage(numeric, () => t('errors.fields.onlyNumbers'))
    }),
    modifiers: {
      immediateDirty: 'lazy-non-empty'
    }
  });

  return useRegle;
}
